import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, Tooltip } from '@mui/material';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { emailSendSelectByQuery, EmailSendFrontend } from '@common-reducers/DBServiceThunks';
import moment from 'moment';
import { useMemo } from 'react';

interface EmailsTabProps {
    supportedUserId: string;
}

const statusColor: Record<string, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
    queued: 'default',
    sent: 'info',
    delivered: 'success',
    bounced: 'error',
    complained: 'warning',
};

const typeColor: Record<string, 'primary' | 'secondary'> = {
    transactional: 'primary',
    marketing: 'secondary',
};

export const EmailsTab = ({ supportedUserId }: EmailsTabProps) => {
    const allEmailSends = useAppSelector(state => emailSendSelectByQuery(state.DBEmailSendReducer, {})) || [];

    const emails = useMemo(() => {
        return allEmailSends
            .filter((e: EmailSendFrontend) => e.userId === supportedUserId)
            .sort((a: EmailSendFrontend, b: EmailSendFrontend) => {
                const dateA = a.sentAt || a.queuedAt;
                const dateB = b.sentAt || b.queuedAt;
                return new Date(dateB).getTime() - new Date(dateA).getTime();
            });
    }, [allEmailSends, supportedUserId]);

    if (emails.length === 0) {
        return <Typography color="text.secondary">No emails found for this user.</Typography>;
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Opened</TableCell>
                        <TableCell>Clicked</TableCell>
                        <TableCell>Opens</TableCell>
                        <TableCell>Clicks</TableCell>
                        <TableCell>Flow</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {emails.map((email: EmailSendFrontend) => (
                        <TableRow key={email._id}>
                            <TableCell>
                                <Tooltip title={moment(email.sentAt || email.queuedAt).format('YYYY-MM-DD HH:mm:ss')}>
                                    <span>{moment(email.sentAt || email.queuedAt).fromNow()}</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={email.type}
                                    size="small"
                                    color={typeColor[email.type] || 'default'}
                                    sx={{ fontSize: '0.75rem', height: '22px' }}
                                />
                            </TableCell>
                            <TableCell>{email.template}</TableCell>
                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <Tooltip title={email.subject}>
                                    <span>{email.subject}</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={email.status}
                                    size="small"
                                    color={statusColor[email.status] || 'default'}
                                    sx={{ fontSize: '0.75rem', height: '22px' }}
                                />
                            </TableCell>
                            <TableCell>
                                {email.firstOpenedAt
                                    ? moment(email.firstOpenedAt).fromNow()
                                    : '\u2014'}
                            </TableCell>
                            <TableCell>
                                {email.firstClickedAt
                                    ? moment(email.firstClickedAt).fromNow()
                                    : '\u2014'}
                            </TableCell>
                            <TableCell>{email.opens?.length || 0}</TableCell>
                            <TableCell>{email.clicks?.length || 0}</TableCell>
                            <TableCell>
                                {email.flowId ? (
                                    <Tooltip title={`Step: ${email.stepId || 'N/A'}`}>
                                        <span>{email.flowId}</span>
                                    </Tooltip>
                                ) : '\u2014'}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
